import log from 'loglevel'
import axios, { type AxiosHeaders } from 'axios'
import {
  filter,
  groupBy,
  isNil,
  keys,
  map,
  omit,
  reduce,
  size,
  sortBy,
  uniq,
  uniqBy
} from 'lodash/fp'

import { url, getCustomHeaders } from 'components/DataProvider/functions'
import { assign } from 'lodash'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import xlsx from 'xlsx-populate'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import { type ConfigProps } from 'components/Dashboard'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const doDownload = async (
  partner: Partial<Partner> | undefined
): Promise<number | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders(
    {},
    !isNil(partner) ? partner.id : undefined
  ) as AxiosHeaders | undefined
  if (isNil(customHeaders)) return undefined
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  if (!isNil(partner)) {
    assign(headers, { 'X-Partner': partner.id })
  }
  const { status } = await axios.post(
    `${url}/users/download`,
    {},
    {
      headers
    }
  )
  return status
}
export const doCognitoDownload = async (): Promise<number | undefined> => {
  if (isNil(url)) return undefined
  const headers = getCustomHeaders({}, undefined) as AxiosHeaders | undefined
  if (isNil(headers)) return undefined
  const { status } = await axios.post(
    `${url}/cognito/users/download`,
    {},
    {
      headers: { Authorization: headers.get('Authorization') }
    }
  )
  return status
}
export const doSolarAccountsDownload = async (
  partner: Partial<Partner>
): Promise<{
  mappings:
    | Array<{
        area: string | null | undefined
        office: string | null | undefined
      }>
    | undefined
  accounts:
    | Array<{
        name: string
        email: string
        group1: string
        group2: string
        status: string
      }>
    | undefined
}> => {
  if (isNil(url)) return { mappings: undefined, accounts: undefined }
  const customHeaders = getCustomHeaders({}, partner.id) as
    | AxiosHeaders
    | undefined
  if (isNil(customHeaders)) return { mappings: undefined, accounts: undefined }
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  assign(headers, { 'X-Partner': partner.id })
  const { status, data } = await axios.get(`${url}/solarAccounts`, {
    headers
  })
  return status === 200 ? data : { mappings: undefined, accounts: undefined }
}
export const doMappingsDownload = async (
  partner: Partial<Partner>
): Promise<{
  mappings:
    | Array<{
        id: string
        name: string
        address: string
        kind: string
        店番: string
        managedBy: string
      }>
    | undefined
  accounts: Array<{ name: string }> | undefined
  ownerAccounts: Array<{ name: string }> | undefined
  format: string | undefined
}> => {
  if (isNil(url))
    return {
      mappings: undefined,
      accounts: undefined,
      ownerAccounts: undefined,
      format: undefined
    }
  const customHeaders = getCustomHeaders({}, partner.id) as
    | AxiosHeaders
    | undefined
  if (isNil(customHeaders))
    return {
      mappings: undefined,
      accounts: undefined,
      ownerAccounts: undefined,
      format: undefined
    }
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  assign(headers, { 'X-Partner': partner.id })
  const { status, data } = await axios.get(`${url}/mappings`, {
    headers
  })
  return status === 200
    ? data
    : {
        mappings: undefined,
        accounts: undefined,
        ownerAccounts: undefined,
        format: undefined
      }
}
export const setMappingsValidation = async (
  data: Uint8Array,
  mappings: Array<Record<string, string>>,
  accounts: Array<{ name: string }>,
  ownerAaccounts: Array<{ name: string }>
): Promise<Uint8Array> => {
  const workbook = await xlsx.fromDataAsync(data)
  const sheet = workbook.sheet('拠点一覧')
  for (let i = 0; i < size(mappings); i++) {
    sheet.cell(`F${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '担当者',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '担当者',
      operator: 'String',
      formula1: `担当者一覧!$A$2:$A$${size(accounts) + 2}`,
      formula2: 'String'
    })
    sheet.cell(`G${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: 'オーナー',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: 'オーナー',
      operator: 'String',
      formula1: `オーナー一覧!$A$2:$A$${size(ownerAaccounts) + 2}`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const setGroupValidation = async (
  data: Uint8Array,
  accounts: Array<{
    name: string
    email: string
    group1: string
    group2: string
    status: string
  }>
): Promise<Uint8Array> => {
  const workbook = await xlsx.fromDataAsync(data)
  const sheet = workbook.sheet('Sheet1')
  const endRow1 = workbook
    .sheet('役割1')
    .usedRange()
    ?.endCell()
    .rowNumber() as number
  const endRow2 = workbook
    .sheet('役割2')
    .usedRange()
    ?.endCell()
    .rowNumber() as number
  for (let i = 0; i < size(accounts); i++) {
    sheet.cell(`C${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '役割1',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '役割1',
      operator: 'String',
      formula1: `役割1!$A$1:$A$${endRow1}`,
      formula2: 'String'
    })
    sheet.cell(`D${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '役割2',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '役割2',
      operator: 'String',
      formula1: `役割2!$A$1:$A$${endRow2}`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const setAdditionalValidations = async (
  data: Uint8Array,
  mappings: Array<{
    area: string | null | undefined
    office: string | null | undefined
  }>,
  accounts: number,
  title: { area: string; office: string }
): Promise<Uint8Array | undefined> => {
  const { allArea, allOffices } = reduce(
    (
      result: {
        allArea: Array<{ area: string }>
        allOffices: Array<{ area: string; office: string }>
      },
      item: {
        area: string | null | undefined
        office: string | null | undefined
      }
    ) => {
      const { area, office } = item
      if (!isNil(area)) {
        result.allArea.push({ area })
        if (!isNil(office)) result.allOffices.push({ area, office })
      }
      return result
    },
    { allArea: [], allOffices: [] }
  )(mappings)
  const areaItems = sortBy<{ area: string }>('area')(
    uniqBy<{ area: string }>('area')(allArea)
  )
  const xlsxData = await json2xlsx(areaItems, undefined, {
    sheet: '一覧1',
    template: data,
    noHeader: true,
    startRow: 1,
    startColumn: 1,
    noFreezePanes: true
  })
  if (isNil(xlsxData)) return undefined
  const workbook = await xlsx.fromDataAsync(xlsxData)
  workbook.addSheet('一覧2')
  const officeItems = groupBy<{ area: string; office: string }>('area')(
    allOffices
  )
  for (let i = 0; i < size(areaItems); i++) {
    if (isNil(areaItems[i])) continue
    const { area } = areaItems[i] as { area: string }
    const offices = uniq(
      map((item: { area: string; office: string }) => item.office)(
        officeItems[area]
      )
    )
    if (isNil(offices)) continue
    const range = workbook.sheet('一覧2').range(1, i + 1, size(offices), i + 1)
    range.value(map((office: string) => [office])(offices))
    workbook.definedName(area, range)
  }
  const sheet = workbook.sheet('Sheet1')
  for (let i = 0; i < accounts; i++) {
    sheet.cell(`E${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: title.area,
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: title.area,
      operator: 'String',
      formula1: `一覧1!$A$1:$A$${size(areaItems)}`,
      formula2: 'String'
    })
    sheet.cell(`F${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: title.office,
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: title.office,
      operator: 'String',
      formula1: `INDIRECT(E${i + 2})`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const createSolarAccountsXlsx = async (
  mappings: Array<{
    area: string | null | undefined
    office: string | null | undefined
  }>,
  accounts: Array<{
    name: string
    email: string
    group1: string
    group2: string
    status: string
  }>,
  config: ConfigProps
): Promise<Uint8Array | undefined> => {
  const { Accounts } = config
  const { group } = Accounts
  const group1 = map((key: string) => group[key])(
    filter((key: string) => key !== 'admin')(keys(group))
  )
  const group2 = map((key: string) => group[key])(
    filter((key: string) => key === 'admin')(keys(group))
  )
  const { allArea, allOffices } = reduce(
    (
      result: {
        allArea: string[]
        allOffices: Array<{ area: string; office: string }>
      },
      item: {
        area: string | null | undefined
        office: string | null | undefined
      }
    ) => {
      const { area, office } = item
      if (!isNil(area)) {
        result.allArea.push(area)
        if (!isNil(office)) result.allOffices.push({ area, office })
      }
      return result
    },
    { allArea: [], allOffices: [] }
  )(mappings)
  const areaItems = sortBy<string>((value) => value)(uniq(allArea))
  const officeItems = groupBy<{ area: string; office: string }>('area')(
    allOffices
  )
  const allOfficeItems = map((area: string) => ({
    name: area,
    refersTo: uniq(
      map((item: { area: string; office: string }) => item.office)(
        officeItems[area]
      )
    )
  }))(areaItems)
  const { Mappings: configMappings } = config
  const xlsx = (await json2xlsx(
    map((item: { status: string }) => ({
      ...item,
      status: item.status === 'CONFIRMED' ? '設定済み' : '未設定'
    }))(accounts),
    undefined,
    {
      name: 'アカウント一覧',
      headers: {
        name: '氏名',
        email: 'メールアドレス',
        group1: '役割1',
        group2: '役割2',
        area: configMappings.area,
        office: configMappings.office,
        status: 'ステータス'
      },
      order: ['name', 'email', 'group1', 'group2', 'area', 'office', 'status'],
      validations: [
        {
          column: 3,
          title: '役割1',
          list: group1,
          allowBlank: true
        },
        {
          column: 4,
          title: '役割2',
          list: group2,
          allowBlank: true
        },
        {
          column: 5,
          title: '分類1',
          list: areaItems,
          allowBlank: true
        },
        {
          column: 6,
          title: '分類2',
          indirect: 5,
          allowBlank: true
        }
      ],
      definedNames: {
        title: '分類',
        names: allOfficeItems
      }
    }
  )) as Uint8Array | undefined
  return xlsx
}
export const createMappingsXlsx = async (
  mappings: Array<Record<string, string>>,
  accounts: Array<{ name: string }>,
  ownerAccounts: Array<{ name: string }>,
  config: ConfigProps
): Promise<Uint8Array | undefined> => {
  const { Mappings: configMappings } = config
  log.info({ ownerAccounts })
  const headers = {
    name: '拠点名',
    address: '住所',
    code: configMappings.code,
    kind: '種別',
    managedBy: '担当者'
  }
  const order = ['name', 'address', 'code', 'kind', 'managedBy']
  const validations = [
    {
      column: 5,
      title: '担当者',
      list: map((item: { name: string }) => item.name)(accounts),
      allowBlank: true
    }
  ]
  if (!isNil(configMappings.useOwner) && (configMappings.useOwner as boolean)) {
    assign(headers, { ownedBy: 'オーナー' })
    order.push('ownedBy')
    validations.push({
      column: 6,
      title: 'オーナー',
      list: map((item: { name: string }) => item.name)(ownerAccounts),
      allowBlank: true
    })
  }
  const xlsx = (await json2xlsx(
    !isNil(configMappings.useOwner) && (configMappings.useOwner as boolean)
      ? map((item: Record<string, string>) =>
          isNil(item['ownedBy']) ? { ...item, ownedBy: '' } : item
        )(mappings)
      : map((item: Record<string, string>) => omit(['ownedBy'])(item))(
          mappings
        ),
    undefined,
    {
      name: '拠点一覧',
      headers,
      order,
      validations
    }
  )) as Uint8Array | undefined
  return xlsx
}
